import { Button, Header, Modal } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { Alert, TextField } from '@carsys/ui'
import { ChangeEvent, MutableRefObject, useEffect, useRef, useState } from 'react'
import { Box, FormControlLabel, IconButton, Switch } from '@mui/material'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import { IEmailAttachment } from './ListAttachment'

export interface IAddAttachmentProps {
  open: boolean
  onCancel: () => void
  onConfirm: (attachmentDesc: string, attachmentFile?: File, isEditing?: boolean) => Promise<void>
  isLoading: boolean
  isEditing?: boolean
  editingAttachment?: IEmailAttachment | null
}

enum AllowedFileTypes {
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  BMP = 'image/bmp',
  WEBP = 'image/webp'
}

export const AddAttachment = (props: IAddAttachmentProps) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [description, setDescription] = useState('')
  const [descError, setDescError] = useState(false)
  const [descHelperText, setDHText] = useState('')

  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')
  const [fileError, setFileError] = useState(false)
  const [fileHelperText, setFHText] = useState('')
  const [hideFileNameInput, setHideFNInput] = useState(false)
  const [reuploadFile, setReuploadFile] = useState(false)

  const handleIconClick = () => {
    ;(fileInputRef as MutableRefObject<HTMLInputElement>).current.click()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files as FileList
    const allowedTypes = Object.values(AllowedFileTypes)

    if (files.length > 0) {
      const fileType = files[0].type as AllowedFileTypes

      if (!allowedTypes.includes(fileType)) {
        event.target.value = ''
        setFileErrorFn()
        return notifier.error(t('email_attachment.add_attch_file_allowed'))
      }

      if (files[0].size > 1000000) {
        event.target.value = ''
        setFileErrorFn()
        return notifier.error(t('email_attachment.add_attch_info'))
      }

      setFile(files[0])
      setFileName(files[0].name)
      setFileError(false)
      setFHText('')
    } else {
      setFileErrorFn()
    }
  }

  const handleReuploadSwitchChange = () => {
    if (!reuploadFile) {
      setHideFNInput(false)
      setReuploadFile(true)
    } else {
      setHideFNInput(true)
      setReuploadFile(false)
    }
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget

    if (name === 'description') {
      if (value === '') {
        setDescError(true)
        setDHText(t('email_attachment.add_attch_desc_error'))
      } else {
        setDescError(false)
        setDHText('')
      }

      setDescription(value)
    }
  }

  const setFileErrorFn = () => {
    setFile(null)
    setFileName('')
    setFileError(true)
    setFHText(t('email_attachment.add_attch_file_error'))
  }

  const reset = () => {
    setDescription('')
    setFile(null)
    setFileName('')
    setDescError(false)
    setDHText('')
    setFileError(false)
    setFHText('')
    setHideFNInput(false)
    setReuploadFile(false)
  }

  const handleOnCancel = () => {
    reset()
    props.onCancel()
  }

  const handleOnConfirm = async () => {
    if (!props.isEditing) await props.onConfirm(description, file as File)
    else await props.onConfirm(description, file || undefined, true)

    reset()
  }

  useEffect(() => {
    if (props.isEditing && props.editingAttachment) {
      setDescription(props.editingAttachment.AttachmentDescription)
      setFileName(props.editingAttachment.AttachmentName)
      setHideFNInput(true)
    }
  }, [props.isEditing, props.editingAttachment])

  return (
    <Modal open={props.open} size='small' data-testid='add-email-attachment-modal'>
      <Header content={props.isEditing ? t('email_attachment.edit_attachment_title') : t('global.add_attachment.title')} />
      <Modal.Content>
        <form noValidate>
          <TextField
            name='description'
            value={description}
            label={t('email_attachment.add_attch_desc')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event)}
            helperText={descHelperText}
            error={descError}
            required
            margin='normal'
          />

          {!hideFileNameInput && (
            <Box
              style={{
                display: 'flex',
                width: '100%'
              }}
            >
              <TextField
                name='file'
                value={fileName}
                label={t('email_attachment.add_attch_file')}
                helperText={fileHelperText}
                error={fileError}
                required
                margin='normal'
                fullWidth
              />

              <IconButton color='primary' size='small' onClick={handleIconClick} data-testid='btn-upload-icon'>
                <i className='upload icon' />
              </IconButton>
            </Box>
          )}
          <input
            type='file'
            multiple={false}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept={Object.values(AllowedFileTypes).join(',')}
            data-testid='btn-file-upload'
          />

          {props.isEditing && (
            <FormControlLabel
              value='start'
              control={<Switch color='primary' checked={reuploadFile} onChange={handleReuploadSwitchChange} />}
              label={t('email_attachment.reupload_file')}
              labelPlacement='start'
            />
          )}
        </form>

        <Alert severity='info'>{t('email_attachment.add_attch_info')}</Alert>
      </Modal.Content>
      <Modal.Actions>
        <Button content={t('global.cancel')} icon='cancel' onClick={handleOnCancel} data-testid='btn-cancel' />
        <Button
          content={t('global.confirm')}
          color='blue'
          onClick={handleOnConfirm}
          data-testid='btn-confirm'
          disabled={description.length === 0 || fileName.length === 0}
          loading={props.isLoading}
        />
      </Modal.Actions>
    </Modal>
  )
}
export default AddAttachment
